import { GRID_COLUMNS } from '../config/dashboardConfig';

/**
 * 두 카드가 겹치는지 확인
 */
export const isCardsOverlapping = (card1, card2) => {
  if (!card1 || !card2 || card1.id === card2.id) return false;

  const card1Right = (card1.x || 0) + (card1.sizeW || 1) - 1;
  const card1Bottom = (card1.y || 0) + (card1.sizeH || 1) - 1;
  const card2Right = (card2.x || 0) + (card2.sizeW || 1) - 1;
  const card2Bottom = (card2.y || 0) + (card2.sizeH || 1) - 1;

  return !(
    card1Right < (card2.x || 0) ||
    (card1.x || 0) > card2Right ||
    card1Bottom < (card2.y || 0) ||
    (card1.y || 0) > card2Bottom
  );
};

/**
 * 카드가 그룹 내 다른 카드와 충돌하는지 확인
 */
export const hasCollision = (card, otherCards, excludeCardId = null) => {
  return otherCards.some((otherCard) => {
    if (excludeCardId && otherCard.id === excludeCardId) return false;
    if (!otherCard.enabled) return false;
    return isCardsOverlapping(card, otherCard);
  });
};

/**
 * 그리드 영역 내 유효한 위치인지 확인
 */
export const isValidPosition = (x, y, sizeW, sizeH) => {
  return (
    x >= 1 &&
    y >= 1 &&
    x + sizeW - 1 <= GRID_COLUMNS + 1 &&
    y + sizeH - 1 >= 1
  );
};

/**
 * 드래그 이벤트에서 그리드 좌표 계산
 */
export const calculateGridPosition = (event, gridElement) => {
  if (!gridElement || !event) return null;

  const rect = gridElement.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  // 그리드 간격과 카드 크기 고려 (gap: 24px, 카드 크기 가정)
  const gap = 24;
  const cardMinWidth = (rect.width - gap * (GRID_COLUMNS - 1)) / GRID_COLUMNS;
  const cardMinHeight = 180; // 최소 카드 높이

  // 그리드 좌표 계산 (1부터 시작)
  const gridX = Math.floor(x / (cardMinWidth + gap)) + 1;
  const gridY = Math.floor(y / (cardMinHeight + gap)) + 1;

  return {
    x: Math.max(1, Math.min(gridX, GRID_COLUMNS)),
    y: Math.max(1, gridY),
  };
};

/**
 * 카드를 놓을 수 있는 가장 가까운 유효한 위치 찾기
 */
export const findValidPosition = (targetX, targetY, sizeW, sizeH, existingCards, excludeCardId = null) => {
  let x = targetX;
  let y = targetY;

  // 먼저 정확한 위치 시도
  if (isValidPosition(x, y, sizeW, sizeH)) {
    const testCard = { x, y, sizeW, sizeH };
    if (!hasCollision(testCard, existingCards, excludeCardId)) {
      return { x, y };
    }
  }

  // 주변 위치 탐색 (최대 거리 내)
  const maxSearchDistance = 5;
  for (let distance = 1; distance <= maxSearchDistance; distance++) {
    // 상하좌우 및 대각선 위치 탐색
    const searchOffsets = [
      { dx: 0, dy: -distance }, // 위
      { dx: distance, dy: 0 },  // 오른쪽
      { dx: 0, dy: distance },  // 아래
      { dx: -distance, dy: 0 }, // 왼쪽
      { dx: distance, dy: -distance }, // 오른쪽 위
      { dx: distance, dy: distance },  // 오른쪽 아래
      { dx: -distance, dy: distance }, // 왼쪽 아래
      { dx: -distance, dy: -distance }, // 왼쪽 위
    ];

    for (const offset of searchOffsets) {
      const newX = targetX + offset.dx;
      const newY = targetY + offset.dy;

      if (isValidPosition(newX, newY, sizeW, sizeH)) {
        const testCard = { x: newX, y: newY, sizeW, sizeH };
        if (!hasCollision(testCard, existingCards, excludeCardId)) {
          return { x: newX, y: newY };
        }
      }
    }
  }

  // 유효한 위치를 찾지 못한 경우, 기존 카드들 다음 위치 반환
  let maxY = 1;
  existingCards.forEach((card) => {
    if (card.enabled && card.y && card.sizeH) {
      maxY = Math.max(maxY, card.y + card.sizeH);
    }
  });

  return { x: 1, y: maxY + 1 };
};

